<template>
    <div v-if="content && isFormFieldsReady" ref="wrapper" class="wrapper">
        <div
            v-loading="isLoading"
            class="s-form-registration"
            :class="{ ...className }"
            el-loading-size="48"
            el-loading-background="inversed-secondary"
            :el-loading-text="content.loading || ''"
        >
            <div
                v-if="content.title"
                class="s-form-registration__title"
                :class="{ 's-form-registration__title-accent': content.hasTitleAccent }"
                v-html="content.title"
            />

            <div v-if="content.tooltip?.title" class="form-tooltip">
                {{ content.tooltip.title }}
                <a-tooltip v-if="content.tooltip.text" :content="content.tooltip.text" />
            </div>

            <div v-if="content.subtitles" class="s-form-registration__subtitle">
                <p
                    v-for="(item, i) in content.subtitles"
                    :key="`subtitle-${i}`"
                    class="s-form-registration__subtitle-item"
                    v-html="item"
                />
            </div>
            <ul v-if="content.checklist" class="s-form-registration__checklist">
                <li
                    v-for="(item, i) in content.checklist"
                    :key="`check-${i}`"
                    class="s-form-registration__checklist-item"
                >
                    <a-glyph name="check-square" />
                    <span v-html="item" />
                </li>
            </ul>

            <div v-if="content.link" class="s-form-registration__link" v-html="content.link" />

            <div v-if="info" class="info">
                <p v-if="info.title" class="info-list-title">
                    <span v-html="info.title" />
                    <a-link
                        v-if="info.titleLink"
                        v-bind="{ ...info.titleLink, target: info.titleLink.target || '_blank' }"
                    />
                </p>
                <a-label v-if="info.label" v-bind="info.label">
                    {{ info.label.text }}
                </a-label>
                <ul v-if="isVisibleInfoList" class="info-list">
                    <li
                        v-for="(item, itemIndex) in info?.list"
                        :key="`info-${itemIndex}`"
                        :class="['info-list-item', item.className]"
                    >
                        <a-link v-if="item.link" v-bind="item.link" />
                        <template v-else>
                            <a-glyph v-if="item.glyph" v-bind="item.glyph" />
                            <span v-html="item.text" />
                        </template>
                    </li>
                </ul>
                <p v-if="info.lead" class="info-lead" v-html="info.lead" />
                <p v-if="info.description" class="info-description" v-html="info.description" />
            </div>

            <el-form
                ref="form"
                :rules="rules"
                :model="form"
                :validate-on-rule-change="false"
            >
                <a-row v-for="(elRowItem, count) in listFieldSpliced" :key="`a-row-${count}`" :gutter="16">
                    <template v-for="(item, i) in elRowItem">
                        <div
                            v-if="item.section"
                            :key="item.section"
                            class="s-form-registration__section"
                        >
                            {{ item.section }}
                        </div>
                        <a-col
                            v-if="isVisibleField(item, ['select', 'select-searchable'])"
                            :key="`select-${i}`"
                            :sm="getInputSize(item, 'sm')"
                            :lg="getInputSize(item, 'lg')"
                        >
                            <el-form-item :prop="item.name" :hint="item.hint">
                                <el-select
                                    :key="form.country"
                                    v-bind="labelSelect"
                                    :ref="`${item.name}`"
                                    v-model="form[item.name]"
                                    :filterable="item.type === 'select-searchable'"
                                    :label="formLabelText(item)"
                                    :placeholder="formPlaceholderText(item)"
                                    :multiple-show-items-limit="item.multipleLimit || 1"
                                    :multiple="item.isMultiple"
                                    :name="item.name"
                                    :required="isRequiredSelect(item)"
                                    :popper-class="`el-select-dropdown__form-registration ${item.dropdownClass || ''}`"
                                    :popper-placement="item['popper-placement']"
                                    :popper-max-width="item['popper-max-width']"
                                    @change="onChangeSelect(item)"
                                >
                                    <template v-if="item.required && formElementsDefault?.required" #suffix>
                                        <div class="input-required-label">
                                            {{ formElementsDefault.required }}
                                        </div>
                                    </template>
                                    <template v-if="item?.options?.[0]?.group">
                                        <el-option-group
                                            v-for="(option, optionIndex) in item.options"
                                            :key="`option-group-${optionIndex}`"
                                            :label="option.label"
                                        >
                                            <el-option
                                                v-for="(group, groupIndex) in option.group"
                                                :key="`option-group-item-${groupIndex}`"
                                                :label="group.label"
                                                :sub-label="group['sub-label']"
                                                :value="group.value"
                                            />
                                        </el-option-group>
                                    </template>
                                    <template v-else>
                                        <el-option
                                            v-for="(option, optionIndex) in listOptions(item)"
                                            :key="`option-item-${optionIndex}`"
                                            :label="option.label"
                                            :sub-label="option['sub-label']"
                                            :value="option.value"
                                        />
                                    </template>
                                </el-select>
                            </el-form-item>
                        </a-col>
                        <a-col
                            v-if="isVisibleField(item, ['input', 'textarea'])"
                            :key="`input-${i}`"
                            :sm="getInputSize(item, 'sm')"
                            :lg="getInputSize(item, 'lg')"
                        >
                            <template v-if="item.name === 'phone' && usePhoneInput">
                                <a-phone-input
                                    :prop="item.name"
                                    :settings="phoneInputSettings"
                                    :default-phone="form.phone"
                                    :default-country="form.phoneCountryCode || form.country"
                                    :required="isRequired(item)"
                                    :placeholder="formPlaceholderText(item)"
                                    @change="onPhoneInputChange"
                                >
                                    <template v-if="item.required && formElementsDefault?.required" #suffix>
                                        <div class="input-required-label">
                                            {{ formElementsDefault.required }}
                                        </div>
                                    </template>
                                </a-phone-input>
                            </template>
                            <template v-else>
                                <el-form-item
                                    :prop="item.name"
                                    :hint="inputHint(item)"
                                    :error="emailFieldError(item)"
                                    :class="formItemClass(item)"
                                >
                                    <el-input
                                        :ref="`${item.name}`"
                                        v-model="form[item.name]"
                                        :autosize="item.autosize"
                                        :disabled="isLoading"
                                        :label="formLabelText(item)"
                                        :placeholder="formPlaceholderText(item)"
                                        :maxlength="item.maxlength"
                                        :name="item.name"
                                        :prefix-value="setPrefixValue(item)"
                                        :required="isRequired(item)"
                                        :type="item.type || 'input'"
                                        @blur="checkEmailOptIn(item.name)"
                                        @mouseleave="checkEmailOptIn(item.name)"
                                    >
                                        <template v-if="item.required && formElementsDefault?.required" #suffix>
                                            <div class="input-required-label">
                                                {{ formElementsDefault.required }}
                                            </div>
                                        </template>
                                    </el-input>
                                    <div v-if="item.tooltip" class="el-form-item__tooltip" v-html="item.tooltip" />
                                    <div
                                        v-if="item.name === 'email' && errorMessageAPI"
                                        :class="classErrorMessage(item)"
                                        v-html="errorMessageAPI"
                                    />
                                </el-form-item>
                            </template>
                        </a-col>
                        <a-col
                            v-if="isVisibleField(item, 'password')"
                            :key="`password-${i}`"
                            :sm="getInputSize(item, 'sm')"
                            :lg="getInputSize(item, 'lg')"
                        >
                            <el-form-item :prop="item.name" :hint="item.hint">
                                <el-input
                                    :ref="`${item.name}`"
                                    v-model="form[item.name]"
                                    show-password-icon
                                    type="password"
                                    :name="item.name"
                                    :required="isRequired(item)"
                                    :label="formLabelText(item)"
                                    :placeholder="formPlaceholderText(item)"
                                >
                                    <template v-if="item.required && formElementsDefault?.required" #suffix>
                                        <div class="input-required-label">
                                            {{ formElementsDefault.required }}
                                        </div>
                                    </template>
                                </el-input>
                                <div v-if="item.tooltip" class="el-form-item__tooltip">
                                    {{ item.tooltip }}
                                </div>
                            </el-form-item>
                        </a-col>
                        <a-col v-if="isVisibleField(item, 'radio') && item.list" :key="`radio-${i}`">
                            <el-form-error v-if="isSubmitted && item.required && !userData[item.name]" class="el-radio-error-message">
                                {{ item.error }}
                            </el-form-error>
                            <el-radio
                                v-for="(radio, radioCount) in item.list"
                                :key="`radio-item-${radioCount}`"
                                v-model="form[item.name]"
                                :label="radio.value"
                                :class="radio.class"
                                :align="radio.align"
                                @change="isSubmitted = false"
                            >
                                <template v-if="radio.title" #default>
                                    {{ radio.title }}
                                </template>
                                <template v-if="radio.description" #description>
                                    {{ radio.description }}
                                </template>
                            </el-radio>
                        </a-col>

                        <a-col
                            v-if="isVisibleField(item, 'date-picker')"
                            :key="`date-picker-${i}`"
                            :sm="getInputSize(item, 'sm')"
                            :lg="getInputSize(item, 'lg')"
                        >
                            <el-form-item :prop="item.name">
                                <el-date-picker
                                    v-model="form[item.name]"
                                    :model-value="null"
                                    v-bind="item"
                                />
                            </el-form-item>
                        </a-col>
                    </template>
                </a-row>

                <a-row v-if="hasCheckboxesWEB46049" class="s-form-checkboxes">
                    <p class="s-form-checkboxes-description">
                        {{ content.checkboxes.description }}
                    </p>
                    <div class="s-form-checkboxes-wrapper">
                        <div
                            v-for="(group, groupIndex) in content.checkboxes.groups"
                            :key="`s-form-checkboxes-group-${groupIndex}`"
                            class="s-form-checkboxes-group"
                        >
                            <p class="s-form-checkboxes-title">
                                {{ group.title }}
                            </p>
                            <div class="s-form-checkboxes-list">
                                <el-checkbox
                                    v-for="(item, itemIndex) in group.list"
                                    :key="`s-form-checkboxe-${itemIndex}`"
                                    ref="checkboxesWEB46049"
                                    v-model="group.list[item]"
                                    :name="item.value"
                                >
                                    {{ item.text }}
                                </el-checkbox>
                            </div>
                        </div>
                    </div>
                </a-row>
                <div v-if="content.links" class="s-form-registration__links">
                    <div v-for="(item, i) in content.links" :key="`link-${i}`" class="s-form-registration__links-item">
                        <a-link class="s-form-registration__footer-item" v-bind="item" />
                    </div>
                </div>
                <div v-for="(item, i) in content.notifications" :key="`notification-${i}`">
                    <div v-if="isVisibleNotification(item)" class="s-form-notification-item">
                        <a-glyph v-if="item.glyph" :name="item.glyph" />
                        <span v-html="item.text" />
                    </div>
                </div>
                <div v-if="hasVisibleCheckboxes" class="s-form-registration__checkboxes">
                    <template v-for="item in listCheckboxes">
                        <el-checkbox
                            v-if="isVisibleCheckbox(item)"
                            :key="item.name"
                            v-model="form[item.name]"
                            :name="item.name"
                            :multiline-label="item.multilineLabel"
                            :multiline-label-limit="item.multilineLabelLimit"
                            @change="isSubmitted = false"
                        >
                            <a-dangerous-html :content="formCheckboxText(item)" />
                            <el-form-error v-if="isVisibleErrorCheckbox(item)" class="checkbox-error-message">
                                <span v-html="item.error || formElementsDefault?.errors?.[item.name] || 'Error'" />
                            </el-form-error>
                        </el-checkbox>
                    </template>
                </div>
                <div v-if="hasAgreement" class="agreement">
                    <a-dangerous-html
                        v-for="(item, i) in listCheckboxesLightGDPR"
                        :key="`light-gdpr-notification-${i}`"
                        :content="formCheckboxText(item)"
                    />
                </div>

                <p v-if="content.information" class="information" v-html="content.information" />

                <a-form-honeypot v-if="content.hasHoneypot" ref="honeypot" />

                <form-extra-buttons
                    v-if="hasExtraButtonsFirst"
                    :buttons="content.extraButtons"
                    @button-click="clickExtraButton"
                />

                <a-button
                    v-if="isStepButtonVisible"
                    class="s-form-registration__button"
                    v-bind="stepButton"
                    :event="{ doNotSendGA: true }"
                    :disabled="isButtonDisable"
                    @mousedown.prevent
                    @click="submit"
                />
                <div v-if="content.notes" class="s-form-registration__note">
                    <p
                        v-for="(item, i) in content.notes"
                        :key="i"
                        class="s-form-registration__note-item"
                        :class="item.class || null"
                        v-html="item.title || item"
                    />
                </div>
            </el-form>

            <form-extra-buttons
                v-if="!hasExtraButtonsFirst"
                :buttons="content.extraButtons"
                @button-click="clickExtraButton"
            />

            <p v-if="content.changeAccount" class="change">
                <a-link :text="content.changeAccount" @click="changeAccount()" />
            </p>
            <template v-if="hasRecaptchaInvisible">
                <div class="recaptcha-notification" v-html="formRecaptchaText" />
            </template>
        </div>
    </div>
</template>

<script>
import { get, size, filter, isEmpty, fromPairs, isString } from 'lodash-es';
import { mapState } from 'vuex';
import AButton from '@core/components/button/button.vue';
import ElCheckbox from '@core/components/checkbox/checkbox.vue';
import ACol from '@core/components/col/col.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import ElDatePicker from '@core/components/date-picker/date-picker.vue';
import ElFormError from '@core/components/form/form-error.vue';
import ElFormItem from '@core/components/form/form-item.vue';
import ElForm from '@core/components/form/form.vue';
import AFormHoneypot from '@core/components/form-honeypot/form-honeypot.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ElInput from '@core/components/input/input.vue';
import ALabel from '@core/components/label/label.vue';
import ALink from '@core/components/link/link.vue';
import { Loading } from '@core/components/loading';
import APhoneInput from '@core/components/phone-input/phone-input.vue';
import ElRadio from '@core/components/radio/radio.vue';
import ARow from '@core/components/row/row.vue';
import ElOptionGroup from '@core/components/select/option-group.vue';
import ElOption from '@core/components/select/option.vue';
import ElSelect from '@core/components/select/select.vue';
import ATooltip from '@core/components/tooltip/tooltip.vue';
import breakpoint from '@core/mixins/breakpoint.js';
import form from '@core/mixins/form.js';
import phoneInput from '@core/mixins/phone-input.js';
import recaptcha from '@core/mixins/recaptcha.js';
import states from '@model/const/states.ts';
import { decrypt } from '@utils/cipher';
import utils from '@utils/form.js';
import { unpackKey, serializeFingerprint } from '@utils/key-mask';

import formExtraButtons from './form-extra-buttons.vue';

const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'pointerdown'];

export default {
    name: 'FormRegistration',

    components: {
        AButton,
        ADangerousHtml,
        AFormHoneypot,
        APhoneInput,
        formExtraButtons,
        AGlyph,
        ATooltip,
        ALabel,
        ALink,
        ARow,
        ACol,
        ElForm,
        ElDatePicker,
        ElFormError,
        ElFormItem,
        ElInput,
        ElCheckbox,
        ElOption,
        ElOptionGroup,
        ElSelect,
        ElRadio,
    },

    directives: {
        loading: Loading.directive,
    },

    mixins: [form, utils, recaptcha, phoneInput, breakpoint],

    props: {
        usePhoneInput: {
            type: Boolean,
            default: false,
        },
        content: {
            type: Object,
            default: () => ({}),
        },
        userData: {
            type: Object,
            default: null,
        },

        settingsForm: {
            type: Object,
            default: null,
        },

        mailgunError: {
            type: Object,
            default: null,
        },

        errorMessageAPI: {
            type: String,
            default: '',
        },

        hasAnimate: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['submit', 'submit-error', 'resize', 'change-step', 'hideLogin', 'extra-button-click'],

    data: () => ({
        hash: '',
        wrapperHeight: 0,
        form: {
            state: '',
            country: 'US',
        },
        isFormFieldsReady: false,
        resellerList: [],
        distributorList: [],
        isButtonDisable: false,
        isSubmitted: false,
        isIdle: false,
        idleTimer: null,
        publicDomainEventSent: false,
    }),

    computed: {
        ...mapState({
            countriesData: (state) => state.countries?.items || [],
            points: (state) => get(state, 'slices.items.s-data-centers.points', []),
            queryStringData: (state) => state.trial?.queryStringData || {},
            locationMapping: (state) => get(state, 'slices.items.s-data-centers.locationMapping', []),
        }),

        info() {
            return this.content?.info;
        },

        formFingerprint() {
            return {
                title: '',
                lead: '',
                locatorType: 'trial',
                lang: this.$route?.params?.lang || '',
            };
        },

        countries() {
            const countriesLocalized = this.formElementsDefault.countriesLocalized || {};
            return this.countriesData.map((item) => ({
                value: item.iso_code,
                label: countriesLocalized[item.iso_code] || item.name,
            }));
        },

        dataCenters() {
            return utils.getUniqueDataCentersForPlatform(this.points).map((item) => ({
                value: item.marketoCode,
                label: item.country,
                country: item.country,
                city: item.city,
                countryCode: item.marketoCountry,
            }));
        },

        rules() {
            const result = {};
            this.listField.forEach((item) => {
                const trigger = item.type?.startsWith('select') ? 'change submit blur' : 'blur';
                result[item.name] = {
                    fieldType: item.type,
                    required: this.isItemRequired(item),
                    validator: this.validateFieldValue,
                    errorMessage: this.formErrorText(item),
                    trigger: item.trigger || trigger,
                    isNumber: item.isNumber,
                    minLength: item.minLength,
                    maxLength: item.maxLength,
                };

                if (item.name === 'email') {
                    result[item.name] = {
                        isBusinessOnlyEmail: item.isBusinessOnlyEmail,
                        isBusinessEmailByCountry: item.isBusinessEmailByCountry,
                        errorMessageBusiness: this.businessEmailErrorText,
                        errorMessage: item.error || this.setErrorMessage('notFilledField'),
                        errorMessageMailgun: this.mailgunError,
                        validator: this.validateEmail.bind(this),
                        trigger: item.trigger || 'blur',
                    };
                }

                if (!this.usePhoneInput && item.name === 'phone') {
                    result[item.name] = {
                        validator: this.validatePhone,
                        errorMessage: this.formErrorText(item),
                        trigger: item.trigger || 'blur',
                    };
                }

                if (item.name === 'password') {
                    result[item.name] = {
                        validator: this.validatePassword,
                        errorMessage: this.formErrorText(item),
                        trigger: item.trigger || 'blur',
                    };
                }

                if (item.name === 'login' && this.content.isCloudConsoleLoginFlowEnabled) {
                    result[item.name] = {
                        validator: this.validateLogin,
                        errorMessage: item.error,
                        errorMessageLogin: this.settingsForm.errorMessageLogin,
                    };
                }

                if (this.fitsWeb46734(item)) {
                    result[item.name] = {
                        validator: this.validateNameFieldExtended,
                        errorMessage: this.formErrorText(item),
                    };
                }
            });
            return result;
        },

        className() {
            return {
                [`s-form-registration-version-${this.content.version}`]: this.content.version,
            };
        },

        listField() {
            const changes = this.fieldChangeConfig || {};
            const list = this.content?.fields || [];
            return list.map((item) => {
                const merged = { ...item, ...changes[item.name] };
                return { ...this.businessEmailDefaults(merged), ...merged };
            });
        },

        fieldEmail() {
            return this.listField.find((item) => item.name === 'email') || {};
        },

        fieldChangeConfig() {
            const result = {};
            (this.formSelectFields || []).forEach((field) => {
                const selected = utils.findOptionByValue(field.options, this.form[field.name]);
                if (!selected?.changeField) return;

                Object.entries(selected.changeField).forEach(([name, props]) => {
                    result[name] = { ...(result[name] || {}), ...props };
                });
            });
            return result;
        },

        stepButton() {
            if (!this.content.button) return null;
            const button = { ...this.content.button };
            delete button.showOnSelection;
            return button;
        },

        isStepButtonVisible() {
            if (!this.content.button) return false;
            if (!this.content.button.showOnSelection) return true;
            return this.listField.some((field) => {
                const value = this.form[field.name];
                return Array.isArray(value) ? value.length > 0 : Boolean(value);
            });
        },

        listFieldSpliced() {
            const result = [];
            const list = [...this.listField];
            while (list.length > 0) {
                const count = list[0].name === 'country' ? 3 : 2;
                result.push(list.splice(0, count));
            }
            return result;
        },

        listAgreements() {
            return get(this, 'content.agreements', []);
        },

        listCheckboxes() {
            return filter(this.listAgreements, ['type', 'checkbox']);
        },

        listCheckboxesGDPR() {
            return filter(this.listAgreements, ['isGDPR', true]);
        },

        listCheckboxesLightGDPR() {
            return filter(this.listAgreements, ['isLightGDPR', true]);
        },

        hasAgreement() {
            const comparison = (item) => item.name === 'country';
            return this.listField.find(comparison) && this.form.country && !this.isCountryGDPR;
        },

        hasCheckboxes() {
            const listGDPR = this.listCheckboxesGDPR;
            const list = this.listCheckboxes.filter((item) => !item.isGDPR);
            return size(list) || (size(listGDPR) && this.isCountryGDPR);
        },

        hasCheckboxesWEB46049() {
            return this.content?.checkboxes?.groups?.length;
        },

        hasVisibleCheckboxes() {
            return this.listCheckboxes.some((item) => this.isVisibleCheckbox(item));
        },

        hasExtraButtonsFirst() {
            return Boolean(this.content.extraButtonsFirst);
        },

        isVisibleInfoList() {
            if (!this.isDesktop && this.info?.list?.length > 3) return false;
            return this.info?.list;
        },
        formEventContext() {
            if (!this.hasCheckboxesWEB46049) return '';
            return this.$refs.checkboxesWEB46049
                .filter((item) => item.isChecked)
                .map((item) => item.name)
                .join(',');
        },

        hasBusinessEmailError() {
            return this.fieldEmail.isBusinessOnlyEmail && this.isFreeEmail(this.form.email);
        },

        businessEmailErrorText() {
            return this.fieldEmail.errorBusinessEmail || this.setErrorMessage('invalidEmailBusiness');
        },
    },

    watch: {
        $route(to) {
            const hash = to.hash.replace('#', '');
            if (this.hash === hash) return;
            this.hash = hash;
        },
        mailgunError: {
            async handler(value) {
                if (!value) return;
                if (!this.$refs.form) return;
                await this.$nextTick();
                this.$refs.form.validateField('email');
            },
            immediate: true,
        },
        'form.country': {
            handler(newVal) {
                if (!newVal) return;
                if (newVal === 'US') {
                    this.form.state = states[0]?.value || '';
                } else {
                    this.form.state = '';
                }
                this.getResellerList();
                this.getDistributorList();
            },
            immediate: true,
        },
        'form.email': {
            handler() {
                this.$emit('hideLogin', this.form.email);
                if (this.form.login) return;
                if (!this.errorMessageAPI) return;
                if (this.content?.steps?.length) return;
                this.isButtonDisable = this.form.email === this.userData.email;
            },
        },
        hasBusinessEmailError(hasError) {
            this.isButtonDisable = hasError;
            if (hasError) this.sendPublicDomainErrorEvent();
            else this.publicDomainEventSent = false;
        },
    },

    updated() {
        const height = this.$refs.wrapper?.clientHeight || 0;
        if (this.wrapperHeight !== height) {
            this.wrapperHeight = height;
            this.$emit('resize');
        }

        if (this.content.clearValidateOnUpdate) {
            this.$nextTick(() => {
                this.$refs.form.clearValidate();
            });
        }

        if (this.hash && this.$refs.form) {
            if (this.hash === this.content.sliceID) {
                this.setFocus();
            }
            this.hash = '';
            this.$nextTick(() => {
                this.$refs.form.clearValidate();
            });
        }
    },

    beforeUnmount() {
        window.removeEventListener('keydown', this.onKeyDown);
        if (this.hasAnimate) {
            IDLE_EVENTS.forEach((e) => window.removeEventListener(e, this.resetIdleTimer));
            clearTimeout(this.idleTimer);
        }
    },

    async mounted() {
        document.addEventListener('keydown', this.onKeyDown);

        if (this.hasAnimate) {
            IDLE_EVENTS.forEach((e) => window.addEventListener(e, this.resetIdleTimer, { passive: true }));
            this.idleTimer = setTimeout(() => { this.isIdle = true; }, 6000);
        }

        this.initFields(this.userData || {});

        if (isEmpty(this.userData)) {
            this.init();
            return;
        }

        if (this.accountData) {
            this.setAccountData();
        }

        if (!this.form.email) return;
        if (!('gdpr_opt_in' in this.form)) return;
        await this.checkEmailOptIn('isAuthorized');

        if (this.content.focusNotSet) return;
        this.setFocus();
    },

    methods: {
        businessEmailDefaults(item) {
            if (!item.isBusinessOnlyEmail) return {};

            const { defaultTooltip, ...defaults } = this.formElementsDefault?.isBusinessOnlyEmail || {};
            if (item.useDefaultTooltip && defaultTooltip) defaults.tooltip = defaultTooltip;
            return defaults;
        },

        emailFieldError(item) {
            if (item.name !== 'email' || !this.hasBusinessEmailError) return '';
            return this.businessEmailErrorText;
        },

        sendPublicDomainErrorEvent() {
            if (this.publicDomainEventSent) return;

            this.publicDomainEventSent = true;
            const eventLabel = this.fieldEmail.eventLabel || '';
            if (!eventLabel) return;

            this.sendDataLayer({
                eventAction: 'error',
                eventLabel,
                eventContent: 'public domain email input',
            });
        },

        resetIdleTimer() {
            this.isIdle = false;
            clearTimeout(this.idleTimer);
            this.idleTimer = setTimeout(() => { this.isIdle = true; }, 6000);
        },

        fitsWeb46734(item) {
            if (!this.$route.path.includes('/products/cyber-protect/trial/')) return false;
            if (!this.locale.startsWith('en')) return false;
            return ['first_name', 'last_name'].includes(item.name);
        },

        hasHintForFreeEmail(item) {
            if (item.isBusinessOnlyEmail) return false;
            return item.hintForFreeEmail && this.isFreeEmail(this.form.email);
        },

        inputHint(item) {
            if (this.hasHintForFreeEmail(item)) return item.hintForFreeEmail;
            return item.hint || item.description || '';
        },

        classInputHint(item) {
            return { 'el-form-item-hint-critical': this.hasHintForFreeEmail(item) };
        },

        formItemClass(item) {
            return {
                ...this.classInputHint(item),
                'idle-glow': this.isIdle && item.name === this.firstEmptyInputFieldName,
            };
        },

        classErrorMessage(item) {
            return {
                'el-form-error-api': item,
                [`el-form-error-api-${item.name}`]: item.name,
            };
        },

        clickExtraButton(button) {
            this.$emit('extra-button-click', button);

            if (!button.resetFields) return;

            this.isSubmitted = false;

            this.listCheckboxes.forEach((checkbox) => {
                this.form[checkbox.name] = checkbox.isChecked || false;
            });

            this.$nextTick(() => {
                this.$refs.form.resetFields();
                this.$refs.form.clearValidate();
            });
        },

        async changeAccount() {
            this.$refs.form.clearValidate();
            await this.$store.dispatch('user/logout');
        },

        async decryptCompanies() {
            const { companies, keyCarrier } = this.$store.state.partnersLocator || {};
            if (!companies || !keyCarrier) return null;
            const key = await unpackKey(keyCarrier, serializeFingerprint(this.formFingerprint));
            return JSON.parse(await decrypt(companies, key));
        },

        async getResellerList() {
            const comparison = (f) => f.name === 'reseller' && f.getReseller;
            const field = this.listField.find(comparison);
            if (!field) return;
            try {
                this.isLoading = true;
                this.form.reseller = '';
                this.form.reseller_id = '';
                const data = await this.decryptCompanies();
                const list = (data?.partners || []).filter((item) => item.country === this.form.country);
                const type = field.typeReseller;
                this.resellerList = list
                    .filter((item) => (type ? item[type] : item))
                    .map((item) => ({ value: item.id, label: item.name }));
            } catch (e) {
                this.resellerList = [];
            } finally {
                this.isLoading = false;
            }
        },

        async getDistributorList() {
            const comparison = (f) => f.name === 'distributor' && f.getDistributor;
            const field = this.listField.find(comparison);
            if (!field) return;
            try {
                this.isLoading = true;
                this.form.distributor = '';
                this.form.distributor_id = '';
                const data = await this.decryptCompanies();
                const list = (data?.distributors || []).filter((item) => item.country === this.form.country);
                const type = field.typeDistributor;
                this.distributorList = list
                    .filter((item) => (type ? item[type] : item))
                    .map((item) => ({ value: item.id, label: item.name }));
            } catch (e) {
                this.distributorList = [];
            } finally {
                this.isLoading = false;
            }
        },

        initFields(userData) {
            this.form = {
                ...fromPairs(this.listField.map((el) => [el.name, el.value ?? ''])),
                ...fromPairs(this.listCheckboxes.map((el) => [el.name, el.isChecked || false])),
                ...userData,
            };

            if (this.content.isFormExpressSignup) {
                this.setSearchParamsData();
            }

            this.isFormFieldsReady = true;
        },

        prefillData(key) {
            if (!Object.keys(this.queryStringData)?.length && !localStorage.getItem(`trial_es_${key}`)) return;
            const query = this.queryStringData;
            const unexpectedKeys = {
                first_name: 'firstname',
                last_name: 'lastname',
                tenantId: 'uid',
            };

            const val = query[key] || query[unexpectedKeys[key]] || localStorage.getItem(`trial_es_${key}`);
            if (!val || val === 'undefined') return;

            const normalizedValue = key === 'email' && isString(val) ? val?.replaceAll(' ', '+') : val;
            this.form[key] = normalizedValue;
            localStorage.setItem(`trial_es_${key}`, normalizedValue);
        },

        setSearchParamsData() {
            this.searchParams = new URLSearchParams(this.queryStringData);
            const expectedKeys = new Set([
                'aan',
                'company',
                'email',
                'first_name',
                'last_name',
                'phone',
                'currency',
                'price',
                'opportunity_id',
                'tenantId',
            ]);
            Object.keys(this.form)
                .filter((x) => expectedKeys.has(x))
                .forEach(this.prefillData);
            if (Object.keys(this.$route?.query || {}).length) {
                const SFDCCampaignID = this.searchParams.get('SFDCCampaignID');
                this.$router.replace({ query: SFDCCampaignID ? { SFDCCampaignID } : null });
            }
        },

        isValidGDPR(item) {
            return !item.isGDPR || this.isCountryGDPR;
        },

        hasCheckboxRequired(item) {
            return item.isRequired && !this.form[item.name] && this.isValidGDPR(item);
        },

        init() {
            this.setCountry();
            this.setState();
            this.setDataCenterLocation();
            this.setAccountData();

            if (this.content.focusNotSet) return;
            this.setFocus();
        },

        async submit() {
            this.isSubmitted = true;

            if (this.hasAnimate) {
                const isValid = await this.$refs.form.validate().then(() => true).catch(() => false);
                if (!isValid) {
                    this.isSubmitted = false;
                    this.$emit('submit-error');
                    const fieldName = this.setFocusErrorField();
                    this.$refs.form.fields
                        .filter((f) => f.prop !== fieldName)
                        .forEach((f) => f.clearValidate());
                    return;
                }
            }

            if (this.hasCheckboxes) {
                const required = this.listCheckboxes.find((item) => this.hasCheckboxRequired(item)) || {};
                if (required.error || this.formElementsDefault.errors?.[required.name]) return;
            }

            try {
                this.isButtonDisable = true;

                this.prepareFormToValidate();
                await this.$refs.form.validate();

                const payload = this.form;
                const initialPayloadToLog = { ...this.form };

                const distributor = this.distributorList.find((item) => item.value === this.form.distributor);
                if (distributor) {
                    payload.distributor = distributor.label;
                    payload.distributor_id = distributor.value;
                }

                const reseller = this.resellerList.find((item) => item.value === this.form.reseller);
                if (reseller) {
                    payload.reseller = reseller.label;
                    payload.reseller_id = reseller.value;
                }

                // WEB-42323
                if (this.content.hasHoneypot) {
                    payload.business_address = this.$refs.honeypot.getValue() || false;
                    if (this.$refs.honeypot.isCaptured()) {
                        this.$refs.honeypot.sendGAEvent();
                    }
                }

                // WEB-43432
                if (this.content.isFormEvents) {
                    const sendNotSpEvent = await this.checkIfSuitableForNotSpEvent();
                    if (sendNotSpEvent) {
                        this.sendNotSpEvent();
                    }

                    const term = this.$route.query?.utm_term;
                    if (['form', 'ccform'].includes(term) || window.sessionStorage?.getItem('utm_term')) {
                        payload.SP_validation = !sendNotSpEvent;
                    } else {
                        payload.SP_validation = null;
                    }

                    if (!payload.meeting_requested) {
                        payload.meeting_requested = null;
                    }
                }

                console.log('on submit', { initialPayloadToLog, payload }); // eslint-disable-line

                this.$emit('submit', payload, this.$refs?.form?.$el, this.formEventContext);
            } catch (e) {
                this.setFocusErrorField();
            } finally {
                this.isButtonDisable = false;
            }
        },

        setListPartners(item) {
            if (item.name === 'reseller' && !item.getReseller) return item.options;
            if (item.name === 'distributor' && !item.getDistributor) return item.options;
            const options = item.name === 'reseller' ? this.resellerList : this.distributorList;
            return [item.options[0], ...options || [], item.options[1]].filter(Boolean);
        },

        listOptions(item) {
            if (['distributor', 'reseller'].includes(item.name)) return this.setListPartners(item) || [];

            const list = {
                state: states,
                country: this.countries,
                dc_location: this.dataCenters,
            };

            const field = this.listField.find((el) => el.name === item.name);
            let options = list[item.name] || field.options || this.formListOption(item.name) || [];

            if (field.affix) {
                options = options.map((el) => ({ ...el, value: `${el.value} ${field.affix}` }));
            }

            if (item.name === 'dc_location') {
                options = options.map((el) => {
                    if (!el.city) return el;
                    return el.label.includes(',') ? el : { ...el, label: `${el.country}, ${el.city}` };
                });
            }

            const selectedOption = options.find((el) => el.selected);
            if (selectedOption && !this.form[item.name]) this.form[item.name] = selectedOption.value;

            // WEB-49345
            const value = new URL(window.location.href).searchParams.get(item.name);
            if (value && options.find((el) => el.value === value)) {
                this.form[item.name] = value;
            }

            return options;
        },

        setPrefixValue(item) {
            if (item.placeholder) return '';
            return item.name === 'phone' ? '+' : '';
        },

        setCountry() {
            const isoCode = this.location?.data?.country?.code;
            const hasCountry = this.countries.find((item) => item.value === isoCode);
            this.form.country = hasCountry ? isoCode : 'US';
        },

        findField(name) {
            const fields = this.content.steps?.flatMap((item) => item.fields || item.form?.registration?.fields || []);
            const list = fields || this.listField || [];
            return list.find((item) => item?.name === name);
        },

        setState() {
            const field = this.findField('state');
            if (!field || this.form.country !== 'US') return;
            if (!this.form.state || !states.find((s) => s.value === this.form.state)) {
                this.form.state = this.location?.data?.region?.code || states[0]?.value || '';
            }
        },

        setDataCenterLocation() {
            const field = this.findField('dc_location');
            if (!field) return;

            let location = '';
            if (this.form.country === 'US' && this.form.state) {
                const list = this.locationMapping || [];
                const comparison = (item) => item.states?.includes(this.form.state);
                location = list.find(comparison)?.location || '';
            }

            const dataCenter = this.dataCenters.find((item) => item.countryCode === this.form.country);
            const code = location || dataCenter?.value || '';
            if (!code) return;

            this.form.dc_location = field.affix ? `${code} ${field.affix}` : code;
        },

        isVisibleNotification(item) {
            if (item.visible) {
                if (!item.visible.list || !item.visible.field) {
                    return false;
                }
                return item.visible.list.includes(this.form[item.visible.field]);
            }
            return false;
        },

        isVisibleCheckbox(item) {
            return item.isOptIn ? this.isVisibleOptIn : this.isValidGDPR(item);
        },

        isVisibleErrorCheckbox(item) {
            return this.isSubmitted && item.isRequired && !this.form[item.name];
        },

        getInputSize(item, sizeID) {
            if (item.sizes && item.sizes[sizeID]) {
                return item.sizes[sizeID];
            }

            if ((item.isDynamic && this.showStatesField) || this.showPostalCodeField) {
                return 12;
            }
            return item.size || 12;
        },

        isItemRequired(item) {
            if (item.name === 'message' && this.content.is46514VariantB) {
                return this.form.company_type === 'Corporate End-Customer';
            }
            return item.required;
        },

        onChangeSelect(item) {
            this.setState();
            this.checkEmailOptIn(item.name);
            const affectsEmail = (item.options || []).some(
                (option) => option.isBusinessOnlyEmail || option.changeField?.email,
            );
            if (affectsEmail) {
                this.$refs.form?.clearValidate?.('email');
                this.$refs.form?.validateField?.('email');
            }
        },

        onKeyDown(event) {
            if (event.key === 'Enter') {
                const fields = this.content?.fields || [];
                const field = fields.find((item) => item.name === event.target.name);
                if (field?.type.includes('select')) {
                    event.target.click();
                }
            }
        },
    },
};
</script>

<style lang="pcss" scoped>
.s-form-registration {
    display: flex;
    position: relative;
    flex-direction: column;
    padding: 40px 16px 56px;
    justify-content: center;
    background: var(--av-inversed-primary);

    @media (--viewport-mobile-wide) {
        border-radius: 4px;
        box-shadow: var(--av-shadow-small);
        border: 1px solid var(--av-brand-light);
    }

    @media (--viewport-tablet) {
        padding: 32px;
    }

    &__button {
        width: 100%;
    }

    :deep(.el-form .a-button) {
        &:first-child {
            margin: 0;
        }
        + .a-button {
            margin-top: 16px;
        }
    }

    &__title {
        @mixin title-accent;
        margin: 0 -2px;
        text-align: center;
        color: var(--av-nav-primary);
    }

    &__title-accent {
        @mixin display-accent;
    }

    .form-tooltip {
        @mixin paragraph;
        display: flex;
        margin: 8px 0 0;
        text-align: center;
        align-items: center;
        justify-content: center;
        color: var(--av-fixed-primary);

        .a-tooltip {
            top: 2px;
            position: relative;
            background: transparent;
            margin-inline-start: 8px;
        }
    }

    &__subtitle {
        @mixin lead;
        margin: 8px 0 0;
        text-align: center;

        :deep(a) {
            cursor: pointer;
            text-decoration: none;
            color: var(--av-brand-secondary);

            &:hover {
                color: var(--av-brand-secondary);
            }
        }
    }

    &__checklist {
        margin-top: 16px;
    }

    &__checklist-item {
        @mixin body;
        display: flex;
        align-items: center;

        .a-glyph {
            margin-inline-end: 8px;
            position: relative;
            top: 1px;
            fill: var(--av-fixed-success);
        }
    }

    &__link {
        @mixin paragraph;
        margin-top: 16px;
        text-align: center;
        color: var(--av-fixed-secondary);

        :deep(a) {
            cursor: pointer;
            text-decoration: none;
            color: var(--av-brand-primary);

            &:hover {
                color: var(--av-brand-secondary);
            }
        }
    }

    .info {
        text-align: center;
        margin-top: 24px;
        @media (--viewport-tablet) {
            margin-top: 16px;
        }
        @media (--viewport-desktop) {
            margin-top: 24px;
        }
        .a-label {
            display: flex;
            margin: 0 auto;
            width: max-content;
        }
        .info-list-title {
            margin-bottom: 24px;
            color: var(--av-nav-primary);

            :deep(.a-link) {
                margin-inline-start: 8px;

                .a-link__content {
                    display: flex;
                    align-items: center;

                    & * {
                        margin: 0;
                    }

                    .a-glyph {
                        margin-bottom: -2px;
                    }
                }
            }
        }
        .info-list {
            gap: 8px;
            display: flex;
            overflow: hidden;
            padding: 8px 0;
            justify-content: space-between;
        }
        .info-list-item {
            @mixin note;
            gap: 16px;
            flex: 1 1 0;
            display: flex;
            font-weight: 400;
            align-items: center;
            flex-direction: column;
            color: var(--av-nav-primary);
            @media (--viewport-desktop) {
                gap: 4px;
                white-space: nowrap;
            }
            .a-glyph {
                min-width: 16px;
                fill: var(--av-solid-brand-secondary-light);
            }
            &.active {
                font-weight: 700;
                color: var(--av-fixed-primary);
                .a-glyph {
                    fill: var(--av-brand-primary);
                }
            }
            &.has-background {
                position: relative;
                &:before {
                    top: 7px;
                    content: '';
                    height: 2px;
                    width: 181.63%;
                    position: absolute;
                    border-radius: 2px;
                    background: var(--av-solid-brand-secondary-light);
                }

            }
        }
        .info-lead {
            @mixin lead-accent;
            margin-top: 24px;
            color: var(--av-nav-primary);
        }
        .info-description {
            @mixin paragraph;
            margin-top: 8px;
            color: var(--av-fixed-secondary);
            @media (--viewport-desktop-wide) {
                padding: 0 60px;
            }
        }
    }

    .s-form-checkboxes {
        border-radius: 4px;
        margin-bottom: 16px;
        padding: 8px 16px 24px;
        color: var(--av-fixed-primary);
        background: var(--av-fixed-info-accent);

        .s-form-checkboxes-description {
            @mixin paragraph-accent;
            text-align: center;
        }

        .s-form-checkboxes-wrapper {
            display: flex;
            flex-wrap: wrap;
            margin: 0 -8px;

            .s-form-checkboxes-group {
                width: 50%;
                padding: 0 8px;
                margin-top: 8px;

                &:last-child {
                    width: 100%;

                    .s-form-checkboxes-list {
                        display: flex;

                        .el-checkbox {
                            width: 50%;
                        }
                    }
                }

                :deep(.el-checkbox) {
                    @mixin caption;
                    padding: 4px 0;

                    &:not(:first-child) {
                        margin: 0;
                    }

                    .el-checkbox__content {
                        align-items: flex-start;
                    }
                }
            }

            .s-form-checkboxes-title {
                @mixin caption-accent;
            }

            .s-form-checkboxes-list {
                margin-top: 8px;
            }
        }
    }

    &__section {
        @mixin paragraph;
        padding: 0 8px 16px;
        text-align: center;

        @media (--viewport-tablet) {
            text-align: start;
        }
    }

    .s-form-notification-item {
        @mixin descriptor;
        display: flex;
        padding: 16px;
        position: relative;
        padding-inline-start: 48px;
        padding-inline-end: 24px;
        margin: 0 0 16px;
        background-color: var(--av-solid-brand-secondary-accent);

        &:not(:first-child) {
            margin: 8px 0 16px;
        }

        :deep(a) {
            text-decoration: none;
            color: var(--av-brand-secondary);
        }

        :deep(.a-glyph) {
            min-width: 16px;
            margin-top: 4px;
            margin-inline-start: -24px;
            margin-inline-end: 8px;
        }
    }

    :deep(.el-form) {
        margin: 32px 0 0;

        @media (--viewport-tablet) {
            margin-top: 24px;
        }

        .el-input__label {
            &.is-active {
                font-weight: 700;
            }
        }

        .el-input__container {
            .el-input__wrapper {
                order: 1;
            }

            .el-input__suffix {
                width: auto;
                order: 3;
                gap: 8px;
                flex-shrink: 0;
            }

            .el-input__required {
                order: 2;
                display: flex;
                padding: 0 8px;
            }
        }

        .el-radio-error-message {
            @mixin body;
            justify-content: center;
            color: var(--av-fixed-danger);
        }

        .el-radio {
            width: 100%;
            border-radius: 4px;
            padding: 16px 24px;
            border: 1px solid var(--av-brand-primary);
            &:not(:first-child) {
                margin-top: 16px;
            }
            &:last-child {
                margin-bottom: 16px;
            }
            .el-radio__label {
                @mixin lead-accent;
                color: var(--av-fixed-primary);
            }
            .el-radio__description {
                @mixin paragraph;
                margin-top: 4px;
                color: var(--av-fixed-primary);
            }
            &.el-radio-label-400 {
                .el-radio__label {
                    font-weight: 400;
                }
            }
        }

        .el-form-item__tooltip {
            /* inset-inline-start/end is not needed for this tooltip */
            @mixin caption-accent;
            z-index: 1;
            width: 100%;
            bottom: 100%;
            display: none;
            padding: 16px;
            margin-bottom: 8px;
            border-radius: 4px;
            position: absolute;
            inset-inline-start: 50%;
            transform: translateX(-50%);
            color: var(--av-inversed-primary);
            background: var(--av-fixed-primary);
            box-shadow: var(--av-shadow-regular);

            &::after {
                width: 0;
                height: 0;
                inset-inline-start: 50%;
                transform: translateX(-50%);
                top: 100%;
                content: '';
                display: block;
                position: absolute;
                border-style: solid;
                border-width: 4px 4px 0;
                border-color: var(--av-fixed-primary) rgba(255, 255, 255, 0)
                    rgba(255, 255, 255, 0) rgba(255, 255, 255, 0);
            }
        }

        .el-form-item__error {
            @mixin caption;
            padding-bottom: 16px;
        }

        .el-form-item__hint {
            @mixin caption;
            margin-bottom: 16px;
            color: var(--av-fixed-light);
        }

        .el-form-item {
            &.is-error {
                .el-input__icon {
                    .svg {
                        fill: var(--av-fixed-danger);
                    }
                }
            }
            &.is-focus {
                .el-input__icon {
                    .svg {
                        fill: var(--av-brand-primary);
                    }
                }
                .el-form-item__tooltip {
                    display: block;
                }
            }
        }

        .el-form-error-api {
            @mixin caption-accent;
            position: relative;
            padding-bottom: 16px;
            color: var(--av-fixed-danger);

            a {
                text-decoration: underline;
            }

            & ~ .el-form-item__error-holder {
                display: none;
            }
        }

        .el-form-error-api-login {
            @media (--viewport-tablet) {
                width: 200%;
            }

            .el-form-item__hint {
                @mixin caption;
                color: var(--av-fixed-light);
            }

            .el-textarea__editor {
                @mixin paragraph;
                color: var(--av-fixed-primary);
                font-family: var(--font-family-default);
            }
        }

        .el-textarea {
            &__label {
                @mixin paragraph;
            }
            &.is-active {
                .el-textarea__label {
                    @mixin caption-accent;
                }
            }
        }
    }

    &__error {
        @mixin descriptor;
        display: flex;
        text-align: start;
        align-items: flex-start;
        margin-bottom: 16px;

        :deep(a) {
            text-decoration: underline;
            color: var(--av-fixed-danger);
        }
    }

    &__checkboxes {
        :deep(.el-checkbox) {
            &:last-child {
                margin-bottom: 16px;
            }

            .el-checkbox__label {
                @mixin body;
                padding: 0;
            }

            .el-checkbox__content {
                align-items: flex-start;
            }

            .el-checkbox__control {
                margin: 4px 0 0;
            }
        }

        .checkbox-error-message {
            @mixin body;
            gap: 8px;
            display: flex;
            align-items: flex-start;
            margin-inline-start: -24px;

            :deep(.a-glyph) {
                min-width: 16px;
                margin-top: 4px;
                fill: var(--av-fixed-danger);
            }
        }
    }

    .input-required-label {
        @mixin caption-accent;
        padding: 4px 8px;
        color: var(--av-fixed-critical);
        background: var(--av-fixed-critical-accent);
        border-radius: 4px;
    }

    .agreement {
        @mixin body;
        text-align: center;
        margin-bottom: 16px;
    }

    .information {
        @mixin body;
        text-align: center;
        margin-bottom: 16px;
        color: var(--av-fixed-secondary);
    }

    .change {
        margin: 16px 0 0;
        text-align: center;
    }

    .recaptcha-notification {
        @mixin body;
        margin-top: 16px;
        text-align: center;
        color: var(--av-fixed-secondary);
    }

    &__note {
        margin: 8px 0 0;

        &-item {
            @mixin caption;
            color: var(--av-fixed-light);
            text-align: center;

            &.large {
                margin: 16px 0 0;
                @mixin body;
                color: var(--av-fixed-secondary);
            }

            &.paragraph {
                margin: 16px 0 0;
                @mixin paragraph;
                color: var(--av-fixed-secondary);
            }
        }
        :deep(a) {
            cursor: pointer;
            color: var(--av-brand-primary);
            text-decoration: none;

            &:hover {
                color: var(--av-brand-secondary);
            }
            &:focus {
                background: var(--av-brand-secondary-light);
                color: var(--av-brand-primary);
            }
            &:active {
                background: none;
                color: var(--av-brand-primary);
            }
        }
    }
}

.s-form-registration.s-form-registration-version-42019 {
    overflow: hidden;
    border-radius: 4px;
    padding: 32px 16px 40px;
    border: 1px solid var(--av-brand-light);
    box-shadow: var(--av-shadow-small);

    @media (--viewport-mobile-wide) {
        padding-inline-end: 24px;
        padding-inline-start: 24px;
    }

    @media (--viewport-desktop-wide) {
        padding-inline-end: 48px;
        padding-inline-start: 48px;
    }

    .s-form-registration__subtitle {
        @mixin body;
        margin-top: 16px;
        color: var(--av-fixed-secondary);
    }
}

[dir="rtl"] {
    .s-form-registration {
        :deep(.el-form) {
            margin: 32px 0 0;
        }
        .el-form-item__tooltip {
            inset-inline-start: auto;
            inset-inline-end: 50%;
        }
    }
}

@keyframes idle-pulse {
    0% {
        border-color: rgba(38, 104, 197, 0.3);
        box-shadow: 0 0 0 0 rgba(38, 104, 197, 0);
    }

    10% {
        border-color: #2668c5;
        box-shadow: 0 0 8px 2px rgba(38, 104, 197, 0.15);
    }

    20% {
        border-color: rgba(38, 104, 197, 0.3);
        box-shadow: 0 0 0 0 rgba(38, 104, 197, 0);
    }

    30% {
        border-color: #2668c5;
        box-shadow: 0 0 8px 2px rgba(38, 104, 197, 0.15);
    }

    40% {
        border-color: rgba(38, 104, 197, 0.3);
        box-shadow: 0 0 0 0 rgba(38, 104, 197, 0);
    }

    50% {
        border-color: #2668c5;
        box-shadow: 0 0 8px 2px rgba(38, 104, 197, 0.15);
    }

    60%, 100% {
        border-color: rgba(38, 104, 197, 0.3);
        box-shadow: 0 0 0 0 rgba(38, 104, 197, 0);
    }
}

@keyframes idle-pulse-error {
    0% {
        border-color: rgba(234, 57, 57, 0.3);
        box-shadow: 0 0 0 0 rgba(234, 57, 57, 0);
    }

    10% {
        border-color: rgb(234, 57, 57);
        box-shadow: 0 0 8px 2px rgba(234, 57, 57, 0.15);
    }

    20% {
        border-color: rgba(234, 57, 57, 0.3);
        box-shadow: 0 0 0 0 rgba(234, 57, 57, 0);
    }

    30% {
        border-color: rgb(234, 57, 57);
        box-shadow: 0 0 8px 2px rgba(234, 57, 57, 0.15);
    }

    40% {
        border-color: rgba(234, 57, 57, 0.3);
        box-shadow: 0 0 0 0 rgba(234, 57, 57, 0);
    }

    50% {
        border-color: rgb(234, 57, 57);
        box-shadow: 0 0 8px 2px rgba(234, 57, 57, 0.15);
    }

    60%, 100% {
        border-color: rgba(234, 57, 57, 0.3);
        box-shadow: 0 0 0 0 rgba(234, 57, 57, 0);
    }
}

.idle-glow {
    :deep(.el-input__container) {
        animation: idle-pulse 5s ease-in-out infinite;
    }

    &.is-error {
        :deep(.el-input__container) {
            animation: idle-pulse-error 5s ease-in-out infinite;
        }
    }
}
</style>
